import { useEffect, useRef, useState } from "react";
import Button from "@shared/v2/Button";
import IconButton from "@shared/v2/IconButton";
import Pagination from "@shared/v2/Pagination";
import TextComboInput from "@shared/v2/TextComboInput";
import TextLink from "@shared/v2/TextLink";
import Typography from "@shared/v2/Typography";
import { useDebounce } from "@shared/hookHelpers";
import { MagnifyingGlassSolidV6, PencilSolidV6, TrashSolidV6 } from "@shared/v2/Icomoon";
import { Table, TBody, TD, TH, THead, TR } from "../Reports/components/BusinessDashboard/Table/Table";
import Loading from "../Goals/components/Loader";
import EditTagModal from "./EditTagModal";
import DeleteTagModal from "./DeleteTagModal";
import useTags from "./hooks/useTags";

const Tags = () => {
  const scrollRef = useRef(null);
  const [editTag, setEditTag] = useState(null);
  const [deleteTag, setDeleteTag] = useState(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const { actions, data, loading } = useTags({ search: debouncedSearch });

  useEffect(() => {
    if (!loading) scrollRef.current?.scrollTo({ top: 0 });
  }, [loading]);

  return (
    <div
      id="content"
      className="tw-overflow-hidden tw-p-[12px] tw-max-w-[1200px] tw-mx-auto tw-flex tw-flex-col tw-h-full"
    >
      <div id="account-tags" className="tw-flex tw-flex-col tw-h-full">
        <div className="tw-flex tw-justify-between tw-items-center tw-p-[12px]">
          <Typography variant="headline2" className="tw-font-medium">
            People Tags
          </Typography>
          <div className="tw-flex-1 tw-flex tw-justify-end tw-gap-[16px]">
            <TextComboInput
              containerClassName="tw-flex-1 !tw-max-w-[400px]"
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              placeholder="Search"
              trailing={<MagnifyingGlassSolidV6 className="tw-text-gray-50" />}
            />
            <Button size="medium" onClick={() => setEditTag({})}>
              + New Tag
            </Button>
          </div>
        </div>
        <div className="tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden tw-relative">
          {loading && <Loading />}
          <div className="tw-flex-1 tw-relative tw-overflow-auto" ref={scrollRef}>
            <Table>
              <THead className="tw-sticky tw-top-0 tw-z-2 tw-bg-white">
                <TR>
                  <TH>Tag</TH>
                  <TH>Applied to</TH>
                  <TH>Edit</TH>
                </TR>
              </THead>
              <TBody>
                {data.tags.map((tag) => (
                  <TR key={tag.id} className="hover:tw-bg-gray-5">
                    <TD className="tw-max-w-[400px] tw-overflow-hidden" title={tag.name}>
                      <div className="tw-overflow-hidden tw-text-ellipsis">{tag.name}</div>
                    </TD>
                    <TD>
                      <TextLink
                        href={`/people/all_people?leads_filter[selected_any_all_tags][]=any&leads_filter[selected_tag_filters][]=${tag.id}`}
                        target="_blank"
                      >
                        {tag.peopleCount} {tag.peopleCount !== 1 ? "people" : "person"}
                      </TextLink>
                    </TD>
                    <TD>
                      <div className="tw-flex tw-gap-[8px]">
                        <IconButton title="Edit Tag" schema="tertiary" onClick={() => setEditTag(tag)}>
                          <PencilSolidV6 />
                        </IconButton>
                        <IconButton title="Delete Tag" schema="tertiary" onClick={() => setDeleteTag(tag)}>
                          <TrashSolidV6 />
                        </IconButton>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            {!loading && data.tags.length === 0 && (
              <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
                <Typography>No tags found</Typography>
              </div>
            )}
          </div>
        </div>
        {data.meta && (
          <div className="tw-p-[8px] tw-flex tw-justify-center">
            <Pagination
              currentPage={data.meta.currentPage}
              totalPages={data.meta.totalPages}
              onGoToPage={actions.setPage}
            />
          </div>
        )}
      </div>

      <EditTagModal
        tag={editTag}
        loading={loading}
        onClose={() => setEditTag(null)}
        onSave={(tag) => actions.saveTag(tag).then(() => setEditTag(null))}
      />
      <DeleteTagModal
        tag={deleteTag}
        loading={loading}
        onClose={() => setDeleteTag(null)}
        onDelete={(tag) => actions.deleteTag(tag).then(() => setDeleteTag(null))}
      />
    </div>
  );
};

export default Tags;
